// Motor del Buscaminas: lógica pura y determinista.
// El tablero sale de una semilla y de la primera casilla tocada (que siempre es segura y abre zona),
// así que el servidor puede repetir la partida
// movimiento a movimiento para comprobar el resultado (ver backend/src/games/minesweeperEngine.ts,
// que es una copia exacta de este archivo: si cambias uno, cambia el otro).

export interface BoardConfig {
  cols: number;
  rows: number;
  mines: number;
}

// Una sola dificultad para todos: 16 × 16 con 40 minas (la que cuenta en el ranking)
export const BOARD: BoardConfig = { cols: 16, rows: 16, mines: 40 };

// ─── Azar determinista ────────────────────────────────────────────────────────

// Hash de la semilla (xmur3) → generador mulberry32
function seedToInt(seed: string): number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

function mulberry32(a: number) {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Tablero ─────────────────────────────────────────────────────────────────

export function neighbors(cfg: BoardConfig, i: number): number[] {
  const x = i % cfg.cols;
  const y = Math.floor(i / cfg.cols);
  const out: number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && ny >= 0 && nx < cfg.cols && ny < cfg.rows) out.push(ny * cfg.cols + nx);
    }
  }
  return out;
}

export interface Board {
  cfg: BoardConfig;
  mines: boolean[];
  counts: number[];  // minas alrededor de cada casilla
  first: number;     // primera casilla tocada: ni ella ni sus vecinas tienen mina
}

export function createBoard(cfg: BoardConfig, seed: string, first: number): Board {
  const total = cfg.cols * cfg.rows;
  const rand = mulberry32(seedToInt(seed));

  // La primera casilla y sus vecinas quedan libres para que el primer toque abra una zona
  const safe = new Set([first, ...neighbors(cfg, first)]);
  const candidates: number[] = [];
  for (let i = 0; i < total; i++) if (!safe.has(i)) candidates.push(i);

  // Barajado Fisher-Yates de las casillas candidatas: las primeras N son minas
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }
  const mines = new Array<boolean>(total).fill(false);
  for (let k = 0; k < cfg.mines; k++) mines[candidates[k]] = true;

  const counts = mines.map((_, i) => neighbors(cfg, i).filter((n) => mines[n]).length);
  return { cfg, mines, counts, first };
}

// ─── Partida ─────────────────────────────────────────────────────────────────

export type Status = 'playing' | 'won' | 'lost';

export interface GameState {
  board: Board;
  open: boolean[];
  flags: boolean[];
  opened: number;        // casillas seguras abiertas
  status: Status;
  exploded: number;      // mina pisada (-1 si ninguna)
}

// Acción: r = descubrir, f = bandera, c = abrir alrededor de un número; t = ms desde el primer toque
export interface Move {
  a: 'r' | 'f' | 'c';
  i: number;
  t: number;
}

export function newGame(board: Board): GameState {
  const total = board.cfg.cols * board.cfg.rows;
  const state: GameState = {
    board,
    open: new Array<boolean>(total).fill(false),
    flags: new Array<boolean>(total).fill(false),
    opened: 0,
    status: 'playing',
    exploded: -1,
  };
  return reveal(state, board.first);
}

function cloneState(s: GameState): GameState {
  return { ...s, open: s.open.slice(), flags: s.flags.slice() };
}

function floodOpen(s: GameState, from: number) {
  const { board } = s;
  const stack = [from];
  while (stack.length) {
    const i = stack.pop()!;
    if (s.open[i] || s.flags[i]) continue;
    s.open[i] = true;
    s.opened++;
    if (board.counts[i] === 0) {
      for (const n of neighbors(board.cfg, i)) if (!s.open[n] && !board.mines[n]) stack.push(n);
    }
  }
}

function checkWin(s: GameState) {
  const { cfg } = s.board;
  if (s.status === 'playing' && s.opened === cfg.cols * cfg.rows - cfg.mines) s.status = 'won';
}

export function reveal(state: GameState, i: number): GameState {
  if (state.status !== 'playing' || state.open[i] || state.flags[i]) return state;
  const s = cloneState(state);
  if (s.board.mines[i]) {
    s.status = 'lost';
    s.exploded = i;
    s.open[i] = true;
    return s;
  }
  floodOpen(s, i);
  checkWin(s);
  return s;
}

export function toggleFlag(state: GameState, i: number): GameState {
  if (state.status !== 'playing' || state.open[i]) return state;
  const s = cloneState(state);
  s.flags[i] = !s.flags[i];
  return s;
}

// Abrir alrededor de un número cuando ya tiene marcadas todas sus minas
export function chord(state: GameState, i: number): GameState {
  const { board } = state;
  if (state.status !== 'playing' || !state.open[i] || board.counts[i] === 0) return state;
  const around = neighbors(board.cfg, i);
  if (around.filter((n) => state.flags[n]).length !== board.counts[i]) return state;
  let s = state;
  for (const n of around) {
    if (!s.open[n] && !s.flags[n]) s = reveal(s, n);
    if (s.status === 'lost') break;
  }
  return s;
}

export function applyMove(state: GameState, move: Move): GameState {
  if (move.a === 'r') return reveal(state, move.i);
  if (move.a === 'f') return toggleFlag(state, move.i);
  return chord(state, move.i);
}

// Repite una partida completa (lo usa el servidor para validar el resultado).
// El primer movimiento tiene que ser descubrir: de él sale el tablero. Devuelve null si no lo es.
export function replay(cfg: BoardConfig, seed: string, moves: Move[]): GameState | null {
  const total = cfg.cols * cfg.rows;
  const [first, ...rest] = moves;
  if (!first || first.a !== 'r' || !Number.isInteger(first.i) || first.i < 0 || first.i >= total) return null;
  let s = newGame(createBoard(cfg, seed, first.i));
  for (const m of rest) {
    if (!Number.isInteger(m.i) || m.i < 0 || m.i >= total) break;
    s = applyMove(s, m);
    if (s.status !== 'playing') break;
  }
  return s;
}

export function flagsUsed(state: GameState): number {
  return state.flags.reduce((n, f) => n + (f ? 1 : 0), 0);
}
